import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { PageHeader } from "../components/Common";
import { useAuth } from "../lib/auth";
import { useChatSocket } from "../lib/chatSocket";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function ChatPage() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chat/contacts");
        setContacts(data);
        if (data.length && !active) setActive(data[0]);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    const fetch = async () => {
      try {
        const { data } = await api.get(`/chat/thread/${active.id}`);
        if (alive) setMessages(data);
      } catch (e) { /* ignore */ }
    };
    fetch();
    return () => { alive = false; };
  }, [active]);

  // Live updates via WebSocket — appends new messages to the active thread
  useChatSocket((evt) => {
    if (evt?.type !== "chat:new" || !evt.message) return;
    const m = evt.message;
    if (!active) return;
    const involvesActive =
      (m.sender_id === active.id && m.receiver_id === user.id) ||
      (m.sender_id === user.id && m.receiver_id === active.id);
    if (!involvesActive) return;
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post("/chat/send", { receiver_id: active.id, text: text.trim() });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title="Chat" subtitle="Direct conversations with teachers, parents and students." />

      <div className="grid lg:grid-cols-[280px_1fr] gap-4 bg-white border border-slate-200 rounded-xl overflow-hidden h-[70vh]">
        <aside className="border-r border-slate-200 overflow-y-auto bg-slate-50">
          {contacts.length === 0 && <p className="p-4 text-sm text-slate-500">No contacts yet.</p>}
          {contacts.map((c) => (
            <button key={c.id} onClick={() => setActive(c)} data-testid={`chat-contact-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-white transition-colors ${active?.id === c.id ? "bg-white" : ""}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold text-sm">
                  {c.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.role}</div>
                </div>
              </div>
            </button>
          ))}
        </aside>

        <section className="flex flex-col">
          {active ? (
            <>
              <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold text-sm">
                  {active.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{active.name}</div>
                  <div className="text-xs text-slate-500">{active.role}</div>
                </div>
              </header>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-2 bg-slate-50">
                {messages.map((m) => {
                  const me = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${me ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${me ? "bg-blue-600 text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"}`}>
                        {m.text}
                        <div className={`mt-1 text-[10px] ${me ? "text-blue-100" : "text-slate-400"}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-sm text-slate-500 py-12">Start the conversation 👋</p>}
              </div>
              <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..."
                  data-testid="chat-input"
                  className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none" />
                <button type="submit" data-testid="chat-send"
                  className="px-4 py-2.5 rounded-full bg-blue-600 text-white hover:bg-blue-700">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="h-10 w-10 mx-auto" />
                <p className="mt-2 text-sm">Select a contact to start chatting</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
